import * as React from 'react';
import { API_BASE, api } from '@/lib/api-client';

interface GoogleStatus {
  configured: boolean;
}

/**
 * Asks the API whether Google sign-in is available before rendering anything.
 *
 * The alternative — always showing the button — sends people to a 503 the
 * server already knew about. A deployment with no OAuth client configured
 * should show the password form alone, not a button that fails.
 */
export function useGoogleSignIn(): { available: boolean; checked: boolean } {
  const [available, setAvailable] = React.useState(false);
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    api
      .get<GoogleStatus>('/auth/google/status', controller.signal)
      .then((status) => setAvailable(status.configured))
      // An unreachable API is already reported by the sign-in form itself;
      // here it just means no button.
      .catch(() => setAvailable(false))
      .finally(() => setChecked(true));
    return () => controller.abort();
  }, []);

  return { available, checked };
}

/**
 * Google's own wordmark, inlined.
 *
 * Google's branding guidelines require the official four-colour "G" and one of
 * the sanctioned button styles; a hand-drawn approximation or a bare text link
 * is a guideline violation. Inlined rather than fetched so the button renders
 * with no third-party request from the sign-in page.
 */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * A link, not a button with an `onClick` fetch: the browser must travel to
 * Google and come back to the API's own origin for the session cookies to be
 * set on the right host. An XHR could not set them.
 */
export function GoogleSignInButton({ redirectTo }: { redirectTo?: string }) {
  const href = redirectTo
    ? `${API_BASE}/auth/google?redirectTo=${encodeURIComponent(redirectTo)}`
    : `${API_BASE}/auth/google`;

  return (
    <a
      href={href}
      className="flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
    >
      <GoogleMark />
      Sign in with Google
    </a>
  );
}

/** The "or" rule between the Google button and the password form. */
export function AuthDivider({ label = 'or' }: { label?: string }) {
  return (
    <div className="relative py-1">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-slate-200" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-white px-3 text-xs uppercase tracking-wide text-slate-400">
          {label}
        </span>
      </div>
    </div>
  );
}

/** Maps the `?googleError=` code the callback redirects with to plain words. */
export function googleErrorMessage(code: string | null): string {
  if (!code) return '';
  if (code === 'access_denied') return 'Google sign-in was cancelled.';
  if (code === 'missing_code') return 'That sign-in link was incomplete. Try again.';
  return 'Google sign-in did not complete. Ask an administrator whether your account has access.';
}
